import 'feather-icons'
import React, { useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import styled from 'styled-components'
import { AutoColumn } from '../components/Column'
import ChartSummary from '../components/ChartSummary'
import Header from '../components/Header'
import { BlockedMessageWrapper, BlockedWrapper } from '../components/index'
import Link from '../components/Link'
import PairInformation from '../components/PairInformation'
import { PairTransactionsTable } from '../components/PairTransactionsTable'
import TokenLogo from '../components/TokenLogo'
import { BLOCKED_WARNINGS, PAIR_BLACKLIST, usedChain } from '../constants'
import { useEthPrice } from '../contexts/GlobalData'
import { usePairData, usePairTransactions } from '../contexts/PairData'
import { TYPE } from '../Theme'
import { formattedNum, formattedPercent, getPoolLink, getSwapLink, shortenAddress } from '../utils'
import { ExplorerDataType, getExplorerLink } from '../utils/etherscan'

const DarkPageContainer = styled.div`
  background: #16171c;
  min-height: 100vh;
  padding: 32px 48px 80px;
  margin-top: 40px;
  max-width: 1440px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;

  @media screen and (max-width: 768px) {
    padding: 12px 20px 60px;
    margin-top: 40px;
  }
`

const BackButton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: white;
  margin-bottom: 32px;

  &:hover {
    opacity: 0.8;
  }
`

function PoolPage({ pairAddress, history }) {
  const {
    token0,
    token1,
    reserve0,
    reserve1,
    reserveUSD,
    trackedReserveUSD,
    oneDayVolumeUSD,
    oneDayVolumeUntracked,
    liquidityChangeUSD,
    volumeChangeUSD,
    volumeChangeUntracked,
  } = usePairData(pairAddress)

  useEffect(() => {
    document.querySelector('body').scrollTo(0, 0)
    window.scrollTo({
      behavior: 'smooth',
      top: 0,
    })
  }, []) // Only run once on mount

  const transactions = usePairTransactions(pairAddress)

  const formattedLiquidity = reserveUSD ? formattedNum(reserveUSD, true) : formattedNum(trackedReserveUSD, true)

  // volume
  const volume = !!oneDayVolumeUSD ? formattedNum(oneDayVolumeUSD, true) : formattedNum(oneDayVolumeUntracked, true)
  const usingUtVolume = oneDayVolumeUSD === 0 && !!oneDayVolumeUntracked
  const volumeChange = formattedPercent(!usingUtVolume ? volumeChangeUSD : volumeChangeUntracked)

  // get fees
  const fees =
    oneDayVolumeUSD || oneDayVolumeUSD === 0
      ? usingUtVolume
        ? formattedNum(oneDayVolumeUntracked * 0.003, true)
        : formattedNum(oneDayVolumeUSD * 0.003, true)
      : '-'

  const [ethPrice] = useEthPrice()
  const token0USD =
    token0?.derivedETH && ethPrice ? formattedNum(parseFloat(token0.derivedETH) * parseFloat(ethPrice), true) : ''

  const token1USD =
    token1?.derivedETH && ethPrice ? formattedNum(parseFloat(token1.derivedETH) * parseFloat(ethPrice), true) : ''
  // rates
  const token0Rate = reserve0 && reserve1 ? formattedNum(reserve1 / reserve0) : '-'
  const token1Rate = reserve0 && reserve1 ? formattedNum(reserve0 / reserve1) : '-'

  // formatted symbols for overflow
  const formattedSymbol0 = token0?.symbol.length > 6 ? token0?.symbol.slice(0, 5) + '...' : token0?.symbol
  const formattedSymbol1 = token1?.symbol.length > 6 ? token1?.symbol.slice(0, 5) + '...' : token1?.symbol
  const liquidityChange = formattedPercent(liquidityChangeUSD)

  if (PAIR_BLACKLIST.includes(pairAddress)) {
    return (
      <BlockedWrapper>
        <BlockedMessageWrapper>
          <AutoColumn gap="1rem" justify="center">
            <TYPE.light style={{ textAlign: 'center' }}>
              {BLOCKED_WARNINGS[pairAddress] ?? `This pair is not supported.`}
            </TYPE.light>
            <Link
              external={true}
              href={getExplorerLink(usedChain, pairAddress, ExplorerDataType.ADDRESS)}
            >{`More about ${shortenAddress(pairAddress)}`}</Link>
          </AutoColumn>
        </BlockedMessageWrapper>
      </BlockedWrapper>
    )
  }

  return (
    <>
      <Header />
      <DarkPageContainer>
        <BackButton onClick={() => history.push('/home')}>
          <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M8.5 16.5L1 9L8.5 1.5"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M1 9H19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span style={{ color: 'white', fontSize: '16px' }}>Back</span>
        </BackButton>
        {/* <RowBetween>
            <TYPE.body>
              <BasicLink to={`/home`}>Home</BasicLink> →
              <BasicLink to={`/pools/${type}`} style={{ marginLeft: '3px' }}>
                {capitalize(type)} {'pools'}
              </BasicLink>
              → {token0?.symbol}-{token1?.symbol}
            </TYPE.body>
          </RowBetween> */}
        {token0 && (
          <>
            {/* Header Section with overlapping tokens and action buttons */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px',
                width: '100%',
                flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                gap: window.innerWidth <= 768 ? '20px' : '0',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  alignItems: 'flex-start',
                  width: '100%',
                }}
              >
                <div style={{ display: 'flex', gap: '14px', alignItems: 'center', width: '100%' }}>
                  {/* Overlapping Token Logos */}
                  <div
                    style={{
                      position: 'relative',
                      display: 'inline-grid',
                      gridTemplateColumns: 'max-content',
                      gridTemplateRows: 'max-content',
                      placeItems: 'start',
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        gridArea: '1 / 1',
                        marginLeft: window.innerWidth <= 768 ? '20px' : '24px',
                        marginTop: '0',
                        position: 'relative',
                        width: window.innerWidth <= 768 ? '40px' : '48px',
                        height: window.innerWidth <= 768 ? '40px' : '48px',
                      }}
                    >
                      <TokenLogo
                        address={token1?.id}
                        size={window.innerWidth <= 768 ? '40px' : '48px'}
                        style={{
                          position: 'absolute',
                          inset: '0',
                          maxWidth: 'none',
                          objectPosition: '50% 50%',
                          objectFit: 'cover',
                          pointerEvents: 'none',
                          width: '100%',
                          height: '100%',
                        }}
                      />
                    </div>
                    <div
                      style={{
                        gridArea: '1 / 1',
                        marginLeft: '0',
                        marginTop: '0',
                        position: 'relative',
                        width: window.innerWidth <= 768 ? '40px' : '48px',
                        height: window.innerWidth <= 768 ? '40px' : '48px',
                      }}
                    >
                      <TokenLogo
                        address={token0?.id}
                        size={window.innerWidth <= 768 ? '40px' : '48px'}
                        style={{
                          position: 'absolute',
                          inset: '0',
                          maxWidth: 'none',
                          objectPosition: '50% 50%',
                          objectFit: 'cover',
                          pointerEvents: 'none',
                          width: '100%',
                          height: '100%',
                        }}
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      fontFamily: 'Inter Display, sans-serif',
                      fontWeight: '700',
                      fontSize: window.innerWidth <= 768 ? '24px' : '32px',
                      color: 'white',
                      margin: '0',
                      lineHeight: '1',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      flex: 1,
                    }}
                  >
                    {token0?.symbol} / {token1?.symbol}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  alignSelf: 'stretch',
                  width: window.innerWidth <= 768 ? '100%' : 'auto',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: window.innerWidth <= 768 ? '12px' : '16px',
                    height: '100%',
                    alignItems: 'center',
                    justifyContent: window.innerWidth <= 768 ? 'stretch' : 'flex-end',
                    width: window.innerWidth <= 768 ? '100%' : '472.5px',
                  }}
                >
                  <Link
                    external
                    href={getPoolLink(token0?.id, token1?.id)}
                    style={{ flex: window.innerWidth <= 768 ? 1 : 'none' }}
                  >
                    <div
                      style={{
                        background: '#222328',
                        boxSizing: 'border-box',
                        display: 'flex',
                        gap: '12px',
                        height: '50px',
                        alignItems: 'center',
                        padding: window.innerWidth <= 768 ? '12px 16px' : '12px 32px',
                        borderRadius: '50px',
                        cursor: 'pointer',
                        border: 'none',
                        width: '100%',
                        justifyContent: 'center',
                      }}
                    >
                      <div
                        style={{
                          fontFamily: 'Inter Display, sans-serif',
                          fontWeight: '500',
                          fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                          color: 'white',
                          whiteSpace: 'nowrap',
                          lineHeight: '1.4',
                        }}
                      >
                        Add Liquidity
                      </div>
                    </div>
                  </Link>
                  <Link
                    external
                    href={getSwapLink(token0?.id, token1?.id)}
                    style={{ flex: window.innerWidth <= 768 ? 1 : 'none' }}
                  >
                    <div
                      style={{
                        background: 'white',
                        boxSizing: 'border-box',
                        display: 'flex',
                        gap: '12px',
                        height: '50px',
                        alignItems: 'center',
                        padding: window.innerWidth <= 768 ? '12px 16px' : '12px 32px',
                        borderRadius: '50px',
                        cursor: 'pointer',
                        border: 'none',
                        width: '100%',
                        justifyContent: 'center',
                      }}
                    >
                      <div
                        style={{
                          fontFamily: 'Inter Display, sans-serif',
                          fontWeight: '500',
                          fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                          color: '#16171c',
                          whiteSpace: 'nowrap',
                          lineHeight: '1.4',
                        }}
                      >
                        Trade
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            </div>

            {/* Exchange Rate Chips */}
            <div
              style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'flex-start',
                marginBottom: '32px',
                flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                width: '100%',
              }}
            >
              <div
                onClick={() => token0?.id && history.push(`/token/${token0.id}`)}
                style={{
                  backdropFilter: 'blur(16px)',
                  boxSizing: 'border-box',
                  display: 'flex',
                  gap: '10px',
                  height: '40px',
                  alignItems: 'center',
                  justifyContent: window.innerWidth <= 768 ? 'flex-start' : 'center',
                  paddingLeft: '8px',
                  paddingRight: '16px',
                  padding: '12px',
                  borderRadius: '32px',
                  position: 'relative',
                  border: '1px solid #222328',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                  width: window.innerWidth <= 768 ? '100%' : 'auto',
                  overflow: 'hidden',
                }}
                onMouseEnter={(e) => (e.target.style.opacity = '0.8')}
                onMouseLeave={(e) => (e.target.style.opacity = '1')}
              >
                <div style={{ position: 'relative', width: '24px', height: '24px', flexShrink: 0 }}>
                  <TokenLogo
                    address={token0?.id}
                    size="24px"
                    style={{
                      position: 'absolute',
                      inset: '0',
                      maxWidth: 'none',
                      objectPosition: '50% 50%',
                      objectFit: 'cover',
                      pointerEvents: 'none',
                      width: '100%',
                      height: '100%',
                    }}
                  />
                </div>
                <div
                  style={{
                    fontFamily: 'Inter Display, sans-serif',
                    fontWeight: '500',
                    fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                    color: 'white',
                    whiteSpace: 'nowrap',
                    lineHeight: '1.4',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  1 {formattedSymbol0} = {token0Rate} {formattedSymbol1}{' '}
                  {parseFloat(token0?.derivedETH) ? '(' + token0USD + ')' : ''}
                </div>
              </div>
              <div
                onClick={() => token1?.id && history.push(`/token/${token1.id}`)}
                style={{
                  backdropFilter: 'blur(16px)',
                  boxSizing: 'border-box',
                  display: 'flex',
                  gap: '10px',
                  height: '40px',
                  alignItems: 'center',
                  justifyContent: window.innerWidth <= 768 ? 'flex-start' : 'center',
                  paddingLeft: '8px',
                  paddingRight: '16px',
                  padding: '12px',
                  borderRadius: '32px',
                  position: 'relative',
                  border: '1px solid #222328',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                  width: window.innerWidth <= 768 ? '100%' : 'auto',
                  overflow: 'hidden',
                }}
                onMouseEnter={(e) => (e.target.style.opacity = '0.8')}
                onMouseLeave={(e) => (e.target.style.opacity = '1')}
              >
                <div style={{ position: 'relative', width: '24px', height: '24px', flexShrink: 0 }}>
                  <TokenLogo
                    address={token1?.id}
                    size="24px"
                    style={{
                      position: 'absolute',
                      inset: '0',
                      maxWidth: 'none',
                      objectPosition: '50% 50%',
                      objectFit: 'cover',
                      pointerEvents: 'none',
                      width: '100%',
                      height: '100%',
                    }}
                  />
                </div>
                <div
                  style={{
                    fontFamily: 'Inter Display, sans-serif',
                    fontWeight: '500',
                    fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                    color: 'white',
                    whiteSpace: 'nowrap',
                    lineHeight: '1.4',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  1 {formattedSymbol1} = {token1Rate} {formattedSymbol0}{' '}
                  {parseFloat(token1?.derivedETH) ? '(' + token1USD + ')' : ''}
                </div>
              </div>
            </div>
            <ChartSummary
              pairAddress={pairAddress}
              token0={token0}
              token1={token1}
              reserve0={reserve0}
              reserve1={reserve1}
              formattedLiquidity={formattedLiquidity}
              liquidityChange={liquidityChange}
              volume={volume}
              volumeChange={volumeChange}
              fees={fees}
            />

            {transactions && (
              <PairTransactionsTable
                items={[...transactions.burns, ...transactions.swaps, ...transactions.mints].sort(
                  (a, b) => +b.transaction.timestamp - +a.transaction.timestamp
                )}
              />
            )}
          </>
        )}

        <PairInformation token0={token0} token1={token1} pairAddress={pairAddress} />
      </DarkPageContainer>
    </>
  )
}

export default withRouter(PoolPage)
